import React, { useState } from 'react'
import { QuizScoringService } from '../../services/quiz/QuizScoringService'
import { BankQuestion } from '../../services/bankQuestionService_moi'
import KaTeXRenderer from '../common/KaTeXRenderer'
import { CheckCircle, XCircle } from 'lucide-react'

const ShortAnswerTest: React.FC = () => {
  const [testResults, setTestResults] = useState<any[]>([])

  const runShortAnswerTests = () => {
    console.log('🧪 Running Short Answer (TL) tests...')
    
    const testCases = [
      {
        name: 'Số nguyên đơn giản',
        question: {
          id: 'tl1',
          type: 'short_answer',
          cau_hoi: 'Một con lắc lò xo dao động điều hòa với phương trình $x = 4\\cos(10\\pi t + \\frac{\\pi}{4})$ cm. Biên độ dao động của con lắc lò xo là',
          dap_an_dung: '4',
          loi_giai: 'Biên độ dao động của vật là 4 cm',
          diem: 1
        } as BankQuestion,
        testAnswers: ['4', '4 cm', '4cm', '', null, '8', '2']
      },
      {
        name: 'Số thập phân với dấu phẩy',
        question: {
          id: 'tl2',
          type: 'short_answer',
          cau_hoi: 'Tính giá trị của π với độ chính xác 2 chữ số thập phân',
          dap_an_dung: '3.14',
          loi_giai: 'Giá trị của π ≈ 3.14',
          diem: 1
        } as BankQuestion,
        testAnswers: ['3,14', '3.14', '3,1416', '3.1416', '3', '314', '']
      },
      {
        name: 'Ký hiệu khoa học',
        question: {
          id: 'tl3',
          type: 'short_answer',
          cau_hoi: 'Khối lượng electron là bao nhiêu kg?',
          dap_an_dung: '9.1e-31',
          loi_giai: 'Khối lượng electron = 9.1 × 10⁻³¹ kg',
          diem: 2
        } as BankQuestion,
        testAnswers: ['9,1e-31', '9.1e-31', '9,1×10^-31', '0.00000000000000000000000000000091', '9.1E-31', '']
      }
    ]
    
    const results = testCases.map(testCase => {
      const questionResults = testCase.testAnswers.map(userAnswer => {
        // Create a mini quiz with just this question
        const questions = [testCase.question]
        const answers = { [testCase.question.id]: userAnswer }
        
        // Score the quiz
        const scoringResult = QuizScoringService.scoreQuiz(questions, answers, 60, 70)
        
        const analysis = scoringResult.detailedAnalysis[0]
        
        return {
          userAnswer,
          isCorrect: analysis?.isCorrect || false,
          points: analysis?.points || 0,
          totalScore: scoringResult.score,
          percentage: scoringResult.percentage
        }
      })
      
      return {
        name: testCase.name,
        question: testCase.question,
        results: questionResults
      }
    })
    
    setTestResults(results)
    console.log('📊 Short Answer test results:', results)
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">Short Answer (TL) Test</h1>
      
      <div className="mb-6">
        <button
          onClick={runShortAnswerTests}
          className="btn-primary px-4 py-2 rounded"
        >
          Run Short Answer Tests
        </button>
      </div>

      {testResults.length > 0 && (
        <div className="space-y-8">
          {testResults.map((testCase, index) => (
            <div key={index} className="border border-gray-300 rounded-lg p-6 bg-white">
              <h2 className="text-xl font-semibold mb-4">{testCase.name}</h2>
              
              {/* Question Display */}
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <div className="font-medium text-gray-700 mb-2">Câu hỏi:</div>
                <div className="text-lg">
                  <KaTeXRenderer content={testCase.question.cau_hoi} />
                </div>
                <div className="mt-2 text-sm text-gray-600">
                  <strong>Đáp án đúng:</strong> {testCase.question.dap_an_dung}
                </div>
                <div className="mt-1 text-sm text-gray-600">
                  <strong>Điểm:</strong> {testCase.question.diem}
                </div>
              </div>
              
              {/* Test Results */}
              <div className="space-y-3">
                <h3 className="font-semibold text-gray-800">Kết quả test với các đáp án khác nhau:</h3>
                
                <div className="grid grid-cols-1 gap-3">
                  {testCase.results.map((result, resultIndex) => (
                    <div
                      key={resultIndex}
                      className={`p-4 rounded-lg border-l-4 ${
                        result.isCorrect 
                          ? 'border-green-500 bg-green-50' 
                          : 'border-red-500 bg-red-50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          {result.isCorrect ? (
                            <CheckCircle className="w-5 h-5 text-green-600" />
                          ) : (
                            <XCircle className="w-5 h-5 text-red-600" />
                          )}
                          <div>
                            <div className="font-medium">
                              Đáp án: <span className="font-mono bg-white px-2 py-1 rounded border">
                                {result.userAnswer === null || result.userAnswer === undefined 
                                  ? 'null' 
                                  : result.userAnswer === '' 
                                    ? '(empty)' 
                                    : String(result.userAnswer)
                                }
                              </span>
                            </div>
                            <div className="text-sm text-gray-600">
                              {result.isCorrect ? 'Chính xác' : 'Sai'} • 
                              Điểm: {result.points}/{testCase.question.diem} • 
                              Tỷ lệ: {result.percentage}%
                            </div>
                          </div>
                        </div>
                        <div className={`text-lg font-bold ${
                          result.isCorrect ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {result.isCorrect ? '✅' : '❌'}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Explanation */}
              {testCase.question.loi_giai && (
                <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="font-medium text-blue-800 mb-2">Lời giải:</div>
                  <div className="text-blue-700">
                    <KaTeXRenderer content={testCase.question.loi_giai} />
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      
      <div className="mt-8 p-4 bg-yellow-50 border border-yellow-200 rounded">
        <h3 className="font-semibold mb-2">📋 Test Features:</h3>
        <ul className="text-sm space-y-1">
          <li>• Kiểm tra số nguyên đơn giản</li>
          <li>• Xử lý dấu phẩy làm phân cách thập phân (3,14 = 3.14)</li>
          <li>• Hỗ trợ ký hiệu khoa học (1,5e-3 = 0.0015)</li>
          <li>• Bỏ qua đơn vị (4 cm = 4)</li>
          <li>• Kiểm tra đáp án rỗng và null</li>
          <li>• Độ chính xác số với tolerance 0.1%</li>
        </ul>
      </div>
    </div>
  )
}

export default ShortAnswerTest